import { memo, useMemo } from 'react';

import { LazyRender, Tabs, TabsProps, TextButton } from '@hcs/design-system';
import { CardTabs, CardTabsProps } from '@hcs/design-system';
import { PlusIcon } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';

import { CHART_TAB_IDS, MACRO_INSIGHTS_FROM_DATE } from '../../constants';
import { ZipHpiChart, ZipListingStatsChart, ZipRpiChart } from '../../features';
import {
  useCerberusStatsFields,
  useZipListingStatsTimeSeries,
} from '../../hooks';
import {
  MARKET_STATS_FOR_USER_GEO_TYPE,
  MarketStateChartsForUserLauncher,
} from '../MarketStateChartsForUser';
import { PropertyTypeWarning } from '../PropertyTypeWarning';
import { GeoTypeChartDropdownMobile } from '../ZipMsaChartDropdownMobile';

import styles from './ZipCharts.module.css';

interface ZipChartsProps {
  className?: string;
  zipcode: string;
  tabType?: 'card' | 'tabs';
  isNarrowView?: boolean;
  scrollContainer?: string | Element | undefined;
  propertyType: 'singleFamily' | 'condo';
  setPropertyType?: (propertyType: 'singleFamily' | 'condo') => void;
}

const dataHcName = 'zip-charts';

export const ZipCharts = memo(
  ({
    className,
    zipcode,
    tabType = 'card',
    isNarrowView,
    scrollContainer,
    propertyType,
    setPropertyType,
  }: ZipChartsProps) => {
    const { isPhoneWidth } = useScreenSize();
    const { data: listingStatsTS, isLoading: isLoadingTSData } =
      useZipListingStatsTimeSeries({
        fromDate: MACRO_INSIGHTS_FROM_DATE,
        zipcode,
      });

    const { data: customFieldsData, isLoading: isLoadingCustomFields } =
      useCerberusStatsFields('chartsZip');

    const {
      tabs,
      cardTabs,
    }: {
      tabs: TabsProps<string | number>['tabs'];
      cardTabs: CardTabsProps['tabs'];
    } = useMemo(() => {
      const macroInsightsContent = (
        <div className={styles.ChartsTabContent}>
          <ZipHpiChart zipcode={zipcode} className={styles.ChartSection} />
          <PropertyTypeWarning
            dataHcName="zip-macro-insights-property-type-toggle"
            propertyType={propertyType}
            setPropertyType={setPropertyType}
          />
          <ZipRpiChart
            zipcode={zipcode}
            propertyType={propertyType}
            className={styles.ChartSection}
          />
        </div>
      );
      const macroInsightsTab = {
        dataHcName: `${dataHcName}-macro-insights`,
        value: CHART_TAB_IDS.MacroInsights,
        tabId: CHART_TAB_IDS.MacroInsights,
        label: <>Macro Insights</>,
        content: macroInsightsContent,
      };

      if (isLoadingTSData || isLoadingCustomFields) {
        return { cardTabs: [macroInsightsTab], tabs: [macroInsightsTab] };
      }

      const hasMarketActivityData =
        listingStatsTS != null && listingStatsTS.length > 0;
      if (hasMarketActivityData) {
        const marketActivityTab = {
          dataHcName: `${dataHcName}-market-activity`,
          value: CHART_TAB_IDS.MarketActivity,
          tabId: CHART_TAB_IDS.MarketActivity,
          label: <>Market Activity</>,
          content: (
            <div className={styles.ChartsTabContent}>
              {customFieldsData?.order.map((cerberusStatsField, index) => {
                const chart = (
                  <ZipListingStatsChart
                    key={cerberusStatsField}
                    zipcode={zipcode}
                    className={styles.ChartSection}
                    cerberusStatsField={cerberusStatsField}
                    propertyType={propertyType}
                    setPropertyType={setPropertyType}
                    showPropertyTypeWarning={index === 0}
                  />
                );
                return scrollContainer ? (
                  <LazyRender
                    key={`${cerberusStatsField}-lazy`}
                    height={520}
                    scrollContainer={scrollContainer}
                    unmountIfInvisible
                    overflow
                    scroll
                  >
                    {chart}
                  </LazyRender>
                ) : (
                  chart
                );
              })}
            </div>
          ),
        };
        return {
          cardTabs: [marketActivityTab, macroInsightsTab],
          tabs: [marketActivityTab, macroInsightsTab],
        };
      } else {
        return {
          cardTabs: [macroInsightsTab],
          tabs: [macroInsightsTab],
        };
      }
    }, [
      customFieldsData,
      isLoadingCustomFields,
      isLoadingTSData,
      zipcode,
      listingStatsTS,
      propertyType,
      setPropertyType,
      scrollContainer,
    ]);
    const chooseFieldsContent = (
      <MarketStateChartsForUserLauncher
        showTooltip={false}
        trigger={
          <TextButton
            dataHcName={`${dataHcName}-data-priority`}
            icon={<PlusIcon size="sm" />}
            noPadding
          >
            Choose Fields
          </TextButton>
        }
        geoType={MARKET_STATS_FOR_USER_GEO_TYPE.ZIP}
      />
    );
    const active =
      tabs.length > 1
        ? CHART_TAB_IDS.MarketActivity
        : CHART_TAB_IDS.MacroInsights;

    return isPhoneWidth || isNarrowView ? (
      <GeoTypeChartDropdownMobile
        chartSelector={chooseFieldsContent}
        market={cardTabs.length === 2 ? cardTabs[0]?.content : undefined}
        macro={
          cardTabs.length === 1 ? cardTabs[0]?.content : cardTabs[1]?.content
        }
      />
    ) : tabType === 'card' ? (
      <CardTabs
        searchParamId={dataHcName}
        dataHcName={dataHcName}
        className={className}
        tabs={cardTabs}
        actionTextConfig={{
          tabId: CHART_TAB_IDS.MarketActivity,
          content: (
            <div className={styles.DataPriorityControlContainer}>
              {chooseFieldsContent}
            </div>
          ),
        }}
        active={
          tabs.length > 1
            ? CHART_TAB_IDS.MarketActivity
            : CHART_TAB_IDS.MacroInsights
        }
      />
    ) : (
      <Tabs
        searchParamId={dataHcName}
        dataHcName={dataHcName}
        className={className}
        tabs={tabs}
        active={active}
      >
        <div className={styles.DataPriorityControlContainer}>
          {chooseFieldsContent}
        </div>
      </Tabs>
    );
  },
);
